/**
 * Cliente mínimo de Server-Sent Events que soporta:
 * - POST con body JSON (EventSource nativo solo hace GET).
 * - Auth via Bearer token.
 * - Eventos tipados con `event:` y `data:` (JSON).
 * - Cancelación via AbortController.
 */
import { useAuthStore } from "@/lib/auth-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export interface SseEvent {
  event: string;
  data: string;
}

export interface SsePostOptions {
  body?: unknown;
  signal?: AbortSignal;
  onEvent: (ev: SseEvent) => void;
  onError?: (err: Error) => void;
}

export async function ssePost(path: string, opts: SsePostOptions): Promise<void> {
  const { body, signal, onEvent, onError } = opts;
  const token = useAuthStore.getState().accessToken;

  const init: RequestInit = {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "text/event-stream",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  const res = await fetch(`${API_BASE_URL}${path}`, init);

  if (!res.ok || !res.body) {
    onError?.(new Error(`SSE HTTP ${res.status}`));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        parseBlock(block, onEvent);
      }
    }
    if (buffer.trim()) parseBlock(buffer, onEvent);
  } catch (err) {
    if ((err as DOMException).name !== "AbortError") {
      onError?.(err as Error);
    }
  }
}

function parseBlock(block: string, onEvent: (ev: SseEvent) => void) {
  let event = "message";
  const dataLines: string[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith(":") || line.length === 0) continue;
    const sep = line.indexOf(":");
    const field = sep === -1 ? line : line.slice(0, sep);
    const value = sep === -1 ? "" : line.slice(sep + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length > 0) {
    onEvent({ event, data: dataLines.join("\n") });
  }
}
